import { DRAW_POSITIONS, TAROT_DECK, THEME_DEFINITIONS } from './tarot-deck.js';
import { ReadingEmailGate } from './reading-email-gate.js?v=20260908-layout';
import {
  SESSION_TTL_MS,
  SESSION_VERSION,
  createBriefSignature,
  createPreview,
  createSessionId,
  drawSpread,
  findReadingVariant,
  normalizeQuestion,
  READING_OPTION_NAMES,
  restoreSession,
  sessionHasExpired,
  serializeSession,
  validateReadingVariantMatrix,
  validateContext,
  validateQuestion
} from './reading-flow-63.js';

function titleCase(value) {
  return value ? `${value[0].toUpperCase()}${value.slice(1)}` : '';
}

function cardProperty(entry) {
  return entry ? `${entry.card.name} — ${titleCase(entry.orientation)}` : '';
}

function canonicalThemeFor(values) {
  const themeId = String(values.theme ?? '').trim();
  const themeLabel = String(values.themeLabel ?? '').trim();
  return THEME_DEFINITIONS.find((theme) => (
    theme.id === themeId && theme.label === themeLabel
  )) ?? null;
}

function allSpreadPositionsRevealed(values) {
  if (!Array.isArray(values.spread) || values.spread.length !== DRAW_POSITIONS.length) return false;
  const revealed = new Set(values.revealedPositions);
  return revealed.size === DRAW_POSITIONS.length
    && DRAW_POSITIONS.every((position) => revealed.has(position));
}

function spreadMatchesCurrentBrief(values) {
  return values.spreadBriefSignature === createBriefSignature({
    theme: values.theme,
    question: values.question,
    context: values.context
  });
}

function interpretationFor(values) {
  const theme = THEME_DEFINITIONS.find((entry) => entry.id === values.theme);
  return createPreview({ theme, question: values.question, spread: values.spread });
}

function spreadCanBeSubmitted(values, now = Date.now()) {
  return allSpreadPositionsRevealed(values)
    && spreadMatchesCurrentBrief(values)
    && Boolean(interpretationFor(values))
    && !sessionHasExpired(values, now);
}

function normalizedLineProperties(properties = {}) {
  return Object.entries(properties ?? {})
    .filter(([, value]) => value !== undefined && value !== null && String(value) !== '')
    .map(([name, value]) => [name, String(value)])
    .sort(([left], [right]) => left.localeCompare(right));
}

const READING_IDENTITY_PROPERTIES = [
  'Theme',
  'Question',
  'Context',
  'Situation',
  'Challenge',
  'Advice',
  'Reading depth',
  'Delivery window'
];

function normalizedIdentityValue(name, value) {
  const normalized = String(value ?? '').trim();
  return name === 'Question' ? normalizeQuestion(normalized) : normalized;
}

function readingIdentity(properties = {}) {
  return READING_IDENTITY_PROPERTIES.map((name) => [
    name,
    normalizedIdentityValue(name, properties[name])
  ]);
}

function lineMatchesSubmission(line, submittedLine) {
  const variantId = line?.variant_id ?? line?.id;
  return String(variantId ?? '') === submittedLine.variantId
    && JSON.stringify(normalizedLineProperties(line?.properties))
      === JSON.stringify(normalizedLineProperties(submittedLine.properties));
}

function lineHasSameReadingIdentity(line, submittedLine) {
  const variantId = line?.variant_id ?? line?.id;
  return Boolean(line?.properties?.['_Reading session'])
    && String(variantId ?? '') === submittedLine.variantId
    && JSON.stringify(readingIdentity(line.properties))
      === JSON.stringify(readingIdentity(submittedLine.properties));
}

export function readingSubmissionProperties(values, submissionId) {
  return {
    ...lineItemProperties(values),
    '_Reading submission': String(submissionId)
  };
}

export function readingResolutionForCart(cart, submittedLine, preferredKey = '') {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const submitted = items.find((line) => (
    String(line?.key ?? '') === String(preferredKey)
    && lineMatchesSubmission(line, submittedLine)
  ));
  if (!submitted?.key) {
    throw new Error('The newly added reading could not be confirmed in the cart. Please try again.');
  }

  const identical = items.filter((line) => lineHasSameReadingIdentity(line, submittedLine));
  const retained = identical.find((line) => String(line.key) !== String(submitted.key)) ?? submitted;
  return {
    updates: Object.fromEntries(identical.map((line) => [
      String(line.key),
      String(line.key) === String(retained.key) ? 1 : 0
    ])),
    retainedKey: String(retained.key),
    submittedKey: String(submitted.key)
  };
}

function cartContainsResolvedReading(cart, submittedLine, resolution) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const identical = items.filter((line) => lineHasSameReadingIdentity(line, submittedLine));
  return identical.length === 1
    && String(identical[0].key) === resolution.retainedKey
    && Number(identical[0].quantity) === 1;
}

export function lineItemProperties(values) {
  const at = (position) => values.spread.find((entry) => entry.position === position);
  const theme = canonicalThemeFor(values);
  const exposeCardIdentities = spreadCanBeSubmitted(values);
  return {
    Theme: theme?.label ?? '',
    Question: values.question,
    Context: values.context,
    Situation: exposeCardIdentities ? cardProperty(at('Situation')) : '',
    Challenge: exposeCardIdentities ? cardProperty(at('Challenge')) : '',
    Advice: exposeCardIdentities ? cardProperty(at('Advice')) : '',
    'Reading depth': values.depth,
    'Delivery window': values.delivery,
    '_Theme card': theme?.cardId ?? '',
    '_Reading session': values.sessionId,
    '_Drawn at': values.drawnAt
  };
}

export function readingSubmitState(variants, values, loading = false) {
  const theme = canonicalThemeFor(values);
  const variant = values.depth && values.delivery && theme
    ? findReadingVariant(variants, values.depth, values.delivery, theme.label)
    : null;
  const unavailable = Boolean(values.depth && values.delivery && theme && !variant?.available);
  const complete = Boolean(
    theme
    && !validateQuestion(values.question)
    && !validateContext(values.context)
    && values.spread?.length === 3
    && spreadCanBeSubmitted(values)
    && values.depth
    && values.delivery
  );
  const ready = complete && Boolean(variant?.available) && !loading;

  let message = 'Complete each step to add this reading to cart.';
  if (unavailable) message = 'That report depth and delivery combination is unavailable.';
  else if (ready) message = '';
  else if (loading) message = 'Adding your reading to cart…';

  return {
    ready,
    variant,
    priceLabel: unavailable ? 'Unavailable' : variant?.priceLabel ?? 'Choose an option',
    label: loading ? 'Adding…' : 'Add to cart · Review your order',
    message
  };
}

function priceLabelForCents(referenceVariant, cents) {
  const sourceAmount = (referenceVariant.price / 100).toFixed(2);
  const targetAmount = (cents / 100).toFixed(2);
  const replaced = referenceVariant.priceLabel.replace(sourceAmount, targetAmount);
  return replaced === referenceVariant.priceLabel ? `$${targetAmount}` : replaced;
}

export function readingPriceBreakdown(
  variants,
  depth,
  delivery,
  theme,
  optionNames = READING_OPTION_NAMES
) {
  const baseVariant = findReadingVariant(variants, depth, '24 hours', theme, optionNames);
  const selectedVariant = findReadingVariant(variants, depth, delivery, theme, optionNames);
  if (!baseVariant || !selectedVariant) return null;
  const deliveryCents = selectedVariant.price - baseVariant.price;
  return {
    basePriceLabel: baseVariant.priceLabel,
    deliveryCents,
    deliveryPriceLabel: deliveryCents === 0
      ? 'Included'
      : `+${priceLabelForCents(selectedVariant, deliveryCents)}`,
    totalPriceLabel: selectedVariant.priceLabel
  };
}

export function createEmptyReadingState(now = Date.now()) {
  return {
    version: SESSION_VERSION,
    expiresAt: now + SESSION_TTL_MS,
    sessionId: createSessionId(),
    theme: '',
    question: '',
    context: '',
    spread: [],
    revealedPositions: [],
    previousCardIds: [],
    spreadBriefSignature: '',
    depth: '',
    delivery: '',
    drawnAt: ''
  };
}

export function applyCompletionDefaults(state) {
  return {
    ...state,
    depth: state.depth || 'Focused',
    delivery: state.delivery || '24 hours'
  };
}

function cardIdsFromSpread(spread) {
  return spread.map((entry) => entry.cardId ?? entry.card?.id).filter(Boolean);
}

export function drawForCurrentBrief(
  state,
  { theme, question, context },
  {
    deck = TAROT_DECK,
    draw = drawSpread,
    now = Date.now(),
    drawnAt = new Date(now).toISOString()
  } = {}
) {
  const brief = {
    theme,
    question: normalizeQuestion(question),
    context: String(context)
  };
  const activeState = sessionHasExpired(state, now)
    ? { ...createEmptyReadingState(now), ...brief }
    : state;
  const currentState = {
    ...activeState,
    ...brief
  };
  const currentBriefSignature = createBriefSignature(brief);
  if (currentState.spread.length === 3 && currentState.spreadBriefSignature === currentBriefSignature) {
    return { action: 'resume', state: currentState };
  }

  const previousCardIds = cardIdsFromSpread(currentState.spread);
  const spread = draw(deck, undefined, previousCardIds);
  return {
    action: 'replace',
    state: {
      ...currentState,
      spread: spread.map((entry) => ({ cardId: entry.card.id, orientation: entry.orientation })),
      revealedPositions: [],
      previousCardIds,
      spreadBriefSignature: currentBriefSignature,
      depth: '',
      delivery: '',
      drawnAt
    }
  };
}

const ConfiguratorElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class ReadingConfigurator extends ConfiguratorElement {
  connectedCallback() {
    if (this.initialized) {
      this.bindPageRestore();
      return;
    }
    this.cacheElements();
    if (!this.canEnhance()) {
      this.showConfigurationState();
      return;
    }
    this.enhance();
    this.restore();
    this.bindEvents();
    this.bindPageRestore();
    this.initialized = true;
    this.applyIncomingFocus();
    const emailGateRoot = this.ownerDocument.querySelector('[data-reading-email-gate]');
    if (emailGateRoot) {
      this.emailGate = new ReadingEmailGate(emailGateRoot);
      if (this.state.spread.length === 3) this.emailGate.recordExperience();
    }
  }

  applyIncomingFocus() {
    const view = this.ownerDocument?.defaultView;
    if (!view) return;
    const url = new URL(view.location.href);
    const focus = url.searchParams.get('focus');
    const input = this.themeInputs.find((entry) => entry.value === focus);
    if (!input) return;
    const panel = this.querySelector('[data-focus-handoff]');
    const consume = () => {
      url.searchParams.delete('focus');
      view.history.replaceState(view.history.state, '', url.href);
      if (panel) panel.hidden = true;
    };
    const apply = () => {
      consume();
      input.checked = true;
      input.dispatchEvent(new view.Event('change', { bubbles: true }));
    };
    const hasDraft = this.state.question || this.state.context || this.state.spread?.length;
    if (hasDraft && this.state.theme !== focus) {
      if (!panel) return;
      panel.querySelector('[data-focus-name]').textContent = THEME_DEFINITIONS.find((theme) => theme.id === focus).label;
      panel.hidden = false;
      panel.querySelector('[data-focus-apply]').addEventListener('click', apply, { once: true });
      panel.querySelector('[data-focus-keep]').addEventListener('click', consume, { once: true });
      return;
    }
    if (hasDraft) { consume(); return; }
    apply();
  }

  disconnectedCallback() {
    if (!this.pageShowTarget || !this.pageShowHandler) return;
    this.pageShowTarget.removeEventListener('pageshow', this.pageShowHandler);
    this.pageShowTarget = null;
  }

  bindPageRestore() {
    if (this.pageShowTarget) return;
    const target = this.ownerDocument?.defaultView ?? window;
    if (!target?.addEventListener) return;
    this.pageShowHandler ??= (event) => {
      if (!event.persisted) return;
      this.clearTransientSubmissionState();
      this.restore({ retainInMemoryOnReadError: true });
    };
    target.addEventListener('pageshow', this.pageShowHandler);
    this.pageShowTarget = target;
  }

  cacheElements() {
    this.form = this.querySelector('form');
    this.fallback = this.querySelector('[data-variant-fallback]');
    this.fallbackSelect = this.querySelector('[data-variant-select]');
    this.configurationStatus = this.querySelector('[data-reading-configuration-status]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.flow = this.querySelector('[data-reading-flow]');
    this.progress = this.querySelector('[data-reading-progress]');
    this.steps = [...this.querySelectorAll('[data-reading-step]')];
    this.themeInputs = [...this.querySelectorAll('[data-theme-input]')];
    this.questionInput = this.querySelector('[data-question-input]');
    this.contextInput = this.querySelector('[data-context-input]');
    this.drawCardsButton = this.querySelector('[data-draw-cards]');
    this.spreadStaleNotice = this.querySelector('[data-spread-stale-notice]');
    this.questionCount = this.querySelector('[data-question-count]');
    this.contextCount = this.querySelector('[data-context-count]');
    this.presets = this.querySelector('[data-question-presets]');
    this.healingNote = this.querySelector('[data-healing-note]');
    this.selectedThemeImage = this.querySelector('[data-selected-theme-image]');
    this.selectedThemeLabel = this.querySelector('[data-selected-theme-label]');
    this.selectedThemeCard = this.querySelector('[data-selected-theme-card]');
    this.cardSlots = [...this.querySelectorAll('[data-card-position]')];
    this.cardRevealButtons = [...this.querySelectorAll('[data-card-reveal]')];
    this.preview = this.querySelector('[data-reading-preview]');
    this.previewFolio = this.querySelector('[data-reading-preview-folio]');
    this.questionPreview = this.querySelector('[data-reading-question-preview]');
    this.revealProgress = this.querySelector('[data-reveal-progress]');
    this.depthBridge = this.querySelector('[data-reading-depth-bridge]');
    this.chooseReadingButton = this.querySelector('[data-go-step="4"]');
    this.depthInputs = [...this.querySelectorAll('[data-depth-input]')];
    this.deliveryInputs = [...this.querySelectorAll('[data-delivery-input]')];
    this.depthPriceNodes = [...this.querySelectorAll('[data-depth-price]')];
    this.deliveryPriceNodes = [...this.querySelectorAll('[data-delivery-price]')];
    this.propertyInputs = [...this.querySelectorAll('[data-property]')];
    this.priceNode = this.querySelector('[data-reading-price]');
    this.orderTitle = this.querySelector('[data-order-title]');
    this.orderBaseLabel = this.querySelector('[data-order-base-label]');
    this.orderBasePrice = this.querySelector('[data-order-base-price]');
    this.orderDeliveryLabel = this.querySelector('[data-order-delivery-label]');
    this.orderDeliveryPrice = this.querySelector('[data-order-delivery-price]');
    this.orderSpread = this.querySelector('[data-order-spread]');
    this.submit = this.querySelector('[data-reading-submit]');
    this.status = this.querySelector('[data-form-status]');
    this.summary = this.querySelector('[data-form-summary]');
    this.startOverButtons = [...this.querySelectorAll('[data-start-over]')];
    this.navigationButtons = [...this.querySelectorAll(
      '[data-back-to-themes], [data-edit-question], [data-resume-cards], [data-go-step]'
    )];
    this.storageKey = this.dataset.storageKey;
    this.cardImagesReady = false;
    this.cardImageLoadKey = '';
    this.cardImageLoadToken = null;
    this.cardImageLoadPromise = null;

    try {
      this.optionNames = JSON.parse(this.querySelector('[data-reading-option-names]')?.textContent ?? '[]');
      this.variants = JSON.parse(this.querySelector('[data-reading-variants]')?.textContent ?? '[]');
      this.cardUrls = JSON.parse(this.querySelector('[data-tarot-urls]')?.textContent ?? '{}');
    } catch {
      this.optionNames = [];
      this.variants = [];
      this.cardUrls = {};
    }
    this.variantContractError = validateReadingVariantMatrix(this.variants, this.optionNames);
  }

  canEnhance() {
    return Boolean(
      this.form
      && this.fallback
      && this.fallbackSelect
      && this.configurationStatus
      && this.variantInput
      && this.flow
      && this.progress
      && this.steps.length === 4
      && this.themeInputs.length === 7
      && this.questionInput
      && this.contextInput
      && this.cardSlots.length === 3
      && this.cardRevealButtons.length === 3
      && this.revealProgress
      && this.previewFolio
      && this.depthBridge
      && this.chooseReadingButton
      && this.submit
      && this.orderBaseLabel
      && this.orderBasePrice
      && this.orderDeliveryLabel
      && this.orderDeliveryPrice
      && this.depthPriceNodes.length === 3
      && this.deliveryPriceNodes.length === 3
      && !this.variantContractError
      && Object.keys(this.cardUrls).length === 79
    );
  }

  showConfigurationState() {
    if (!this.variantContractError || !this.configurationStatus || this.dataset.designMode !== 'true') return;
    this.configurationStatus.textContent = this.variantContractError;
    this.configurationStatus.hidden = false;
  }

  enhance() {
    this.fallback.hidden = true;
    this.fallbackSelect.removeAttribute('name');
    this.flow.hidden = false;
    this.form.noValidate = true;
    this.variantInput.disabled = false;
    [...this.themeInputs, this.questionInput, this.contextInput, ...this.propertyInputs]
      .forEach((control) => { control.disabled = false; });
    this.dataset.enhanced = 'true';
    this.configurationStatus.hidden = true;
  }

  emptyState(now = Date.now()) {
    return createEmptyReadingState(now);
  }

  restore({ retainInMemoryOnReadError = false } = {}) {
    let restored = null;
    let storedRaw = null;
    let storageReadFailed = false;
    try {
      storedRaw = localStorage.getItem(this.storageKey);
      restored = restoreSession(storedRaw, TAROT_DECK);
    } catch {
      storageReadFailed = true;
    }
    if (!storageReadFailed && storedRaw !== null && !restored) {
      try { localStorage.removeItem(this.storageKey); } catch { /* no-op */ }
    }
    if (storageReadFailed && retainInMemoryOnReadError && this.state) {
      const retained = restoreSession(serializeSession(this.state), TAROT_DECK);
      if (retained && (retained.spread.length === 0 || spreadMatchesCurrentBrief(retained))) {
        restored = retained;
      }
    }
    this.state = restored ? { ...restored, drawnAt: restored.drawnAt ?? '' } : this.emptyState();
    this.applyStateToControls();
    const initialStep = this.state.spread.length === 3 && !this.spreadIsStale
      ? (this.previewIsReady && (this.state.depth || this.state.delivery) ? 4 : 3)
      : (this.state.theme ? 2 : 1);
    this.showStep(initialStep, false);
    this.update();
    if (this.status) this.status.textContent = '';
  }

  applyStateToControls() {
    this.restoreConfigurationControls();
    this.renderTheme();
    this.prepareSpreadImages();
    this.renderSpread();
    if (!this.cardImageLoadPromise) this.revealProgress.textContent = '';
    ['theme', 'question', 'context', 'depth', 'delivery'].forEach((key) => this.setError(key, ''));
    if (this.summary) {
      this.summary.hidden = true;
      this.summary.querySelector('[data-form-summary-list]')?.replaceChildren();
    }
    this.updateRevealGate();
    this.updateCounts();
  }

  bindEvents() {
    this.themeInputs.forEach((input) => {
      const resumeSelectedTheme = (event) => {
        if (this.guardLockedInteraction(event)) return;
        if (input.checked && input.value === this.state.theme) {
          if (!this.ensureActiveSession()) return;
          this.showStep(2);
        }
      };
      input.addEventListener('click', resumeSelectedTheme);
      input.addEventListener('keydown', (event) => {
        if (![' ', 'Enter'].includes(event.key)) return;
        if (this.guardLockedInteraction(event)) return;
        if (!input.checked || input.value !== this.state.theme) return;
        event.preventDefault();
        resumeSelectedTheme(event);
      });
    });

    this.form.addEventListener('change', (event) => {
      if (this.guardLockedInteraction(event)) return;
      if (event.target.matches('[data-theme-input]')) {
        this.ensureActiveSession(Date.now(), false);
        this.state.theme = event.target.value;
        this.renderTheme();
        this.updateRevealGate();
        this.save();
        this.showStep(2);
      }
      if (event.target.matches('[data-depth-input], [data-delivery-input]') && !this.ensureActiveSession()) return;
      if (event.target.matches('[data-depth-input]')) this.state.depth = event.target.value;
      if (event.target.matches('[data-delivery-input]')) this.state.delivery = event.target.value;
      this.save();
      this.update();
    });

    this.questionInput.addEventListener('input', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.state.question = this.questionInput.value;
      this.setError('question', '');
      this.updateCounts();
      this.updateRevealGate();
      this.save();
      this.update();
    });
    this.contextInput.addEventListener('input', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.state.context = this.contextInput.value;
      this.setError('context', validateContext(this.state.context));
      this.updateCounts();
      this.updateRevealGate();
      this.save();
      this.update();
    });

    this.querySelectorAll('[data-go-step]').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (this.guardLockedInteraction(event)) return;
        this.showStep(Number(button.dataset.goStep));
      });
    });
    this.querySelector('[data-back-to-themes]')?.addEventListener('click', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.showStep(1);
    });
    this.querySelector('[data-edit-question]')?.addEventListener('click', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.showStep(2);
    });
    this.querySelector('[data-resume-cards]')?.addEventListener('click', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.showStep(3);
    });
    this.querySelector('[data-draw-cards]')?.addEventListener('click', (event) => {
      if (this.guardLockedInteraction(event)) return;
      this.handleDraw();
    });
    this.cardRevealButtons.forEach((button, index) => {
      button.addEventListener('click', (event) => {
        if (this.guardLockedInteraction(event)) return;
        this.revealCard(index);
      });
    });
    this.startOverButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        if (this.guardLockedInteraction(event)) return;
        this.reset();
      });
    });
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  get theme() {
    return THEME_DEFINITIONS.find((theme) => theme.id === this.state.theme) ?? null;
  }

  get spread() {
    return this.state.spread.map((stored, index) => ({
      position: ['Situation', 'Challenge', 'Advice'][index],
      card: TAROT_DECK.find((card) => card.id === stored.cardId),
      orientation: stored.orientation
    })).filter((entry) => entry.card);
  }

  get allCardsRevealed() {
    return this.state.revealedPositions.length === this.state.spread.length
      && this.state.spread.length === 3;
  }

  get currentBriefSignature() {
    return createBriefSignature({
      theme: this.state.theme,
      question: this.questionInput?.value ?? this.state.question,
      context: this.contextInput?.value ?? this.state.context
    });
  }

  get spreadIsStale() {
    return this.state.spread.length === 3
      && this.state.spreadBriefSignature !== this.currentBriefSignature;
  }

  get spreadIsCurrent() {
    return this.state.spread.length === 3 && !this.spreadIsStale;
  }

  get interpretation() {
    return interpretationFor(this.values());
  }

  get previewIsReady() {
    return spreadCanBeSubmitted(this.values());
  }

  renewExpiredSession(now = Date.now(), focus = true) {
    const brief = {
      theme: this.state?.theme ?? '',
      question: this.questionInput?.value ?? this.state?.question ?? '',
      context: this.contextInput?.value ?? this.state?.context ?? ''
    };
    this.cardImageLoadToken = null;
    this.cardImageLoadKey = '';
    this.cardImageLoadPromise = null;
    this.cardImagesReady = false;
    this.state = { ...this.emptyState(now), ...brief };
    this.themeInputs.forEach((input) => { input.checked = input.value === this.state.theme; });
    this.applyPackageControls();
    this.renderTheme();
    this.cardSlots.forEach((slot) => this.renderCardSlot(slot, null, false));
    this.revealProgress.textContent = '';
    this.updateRevealGate();
    this.updateCounts();
    this.save();
    this.update();
    this.showStep(this.state.theme ? 2 : 1, focus);
  }

  ensureActiveSession(now = Date.now(), focus = true) {
    if (!sessionHasExpired(this.state, now)) return true;
    this.renewExpiredSession(now, focus);
    return false;
  }

  renderTheme() {
    const theme = this.theme;
    if (!theme) {
      if (this.selectedThemeImage) {
        this.selectedThemeImage.src = this.cardUrls.back;
        this.selectedThemeImage.alt = '';
      }
      if (this.selectedThemeLabel) this.selectedThemeLabel.textContent = 'Your theme';
      if (this.selectedThemeCard) this.selectedThemeCard.textContent = '';
      if (this.healingNote) this.healingNote.hidden = true;
      this.presets.replaceChildren();
      this.setProperty('Theme', '');
      return;
    }
    const card = TAROT_DECK.find((entry) => entry.id === theme.cardId);
    if (this.selectedThemeImage) {
      this.selectedThemeImage.src = this.cardUrls[theme.cardId] ?? this.cardUrls.back;
      this.selectedThemeImage.alt = card?.name ?? theme.label;
    }
    if (this.selectedThemeLabel) this.selectedThemeLabel.textContent = theme.label;
    if (this.selectedThemeCard) this.selectedThemeCard.textContent = card?.name ?? '';
    if (this.healingNote) this.healingNote.hidden = theme.id !== 'healing';
    this.presets.replaceChildren(...theme.prompts.map((prompt) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'reading-question-preset';
      button.textContent = prompt;
      button.disabled = Boolean(this.requestInFlight);
      button.addEventListener('click', (event) => {
        if (this.guardLockedInteraction(event)) return;
        this.questionInput.value = prompt;
        this.state.question = prompt;
        this.setError('question', '');
        this.updateCounts();
        this.updateRevealGate();
        this.save();
        this.update();
        this.questionInput.focus();
      });
      return button;
    }));
    this.setProperty('Theme', theme.label);
  }

  handleDraw() {
    if (this.requestInFlight) return;
    this.ensureActiveSession(Date.now(), false);
    const questionError = validateQuestion(this.questionInput.value);
    const contextError = validateContext(this.contextInput.value);
    this.setError('question', questionError);
    this.setError('context', contextError);
    if (questionError || contextError) {
      (questionError ? this.questionInput : this.contextInput).focus();
      return;
    }

    const brief = {
      theme: this.state.theme,
      question: this.questionInput.value,
      context: this.contextInput.value
    };
    const newDraw = this.state.spread.length !== 3
      || this.state.spreadBriefSignature !== createBriefSignature(brief);
    if (newDraw && this.emailGate?.request(() => this.handleDraw())) return;
    const result = drawForCurrentBrief(this.state, brief);
    this.state = result.state;
    this.questionInput.value = this.state.question;
    this.updateCounts();
    this.save();
    if (result.action === 'replace') {
      this.emailGate?.recordExperience();
      this.revealProgress.textContent = '';
      this.applyPackageControls();
    }
    this.prepareSpreadImages();
    this.renderSpread();
    this.updateRevealGate();
    this.update();
    this.showStep(3);
  }

  renderSpread() {
    const spread = this.spread;
    this.cardSlots.forEach((slot, index) => {
      const entry = spread[index] ?? null;
      this.renderCardSlot(slot, entry, Boolean(entry && this.state.revealedPositions.includes(entry.position)));
    });
  }

  renderCardSlot(slot, entry, revealed) {
    const button = slot.querySelector('[data-card-reveal]');
    const image = slot.querySelector('[data-card-image]');
    const name = slot.querySelector('[data-card-name]');
    const orientation = slot.querySelector('[data-card-orientation]');
    const keywords = slot.querySelector('[data-card-keywords]');
    const hasEntry = Boolean(entry?.card);
    const preparing = hasEntry && !revealed && this.cardImagesReady === false;

    button.disabled = Boolean(this.requestInFlight) || !hasEntry || preparing;
    button.setAttribute('aria-disabled', String(Boolean(this.requestInFlight) || !hasEntry || revealed || preparing));
    button.setAttribute('aria-pressed', String(revealed));
    button.classList.toggle('is-revealed', Boolean(revealed));
    image.classList.remove('is-reversed', 'is-image-fallback');
    image.onerror = null;

    if (!hasEntry || !revealed) {
      const position = entry?.position ?? slot.dataset.cardPosition;
      if (position) button.setAttribute('aria-label', `Reveal ${position} card`);
      image.src = this.cardUrls.back;
      image.alt = '';
      name.textContent = 'Tap to reveal';
      orientation.textContent = '';
      keywords.textContent = '';
      return;
    }

    const cardKeywords = entry.orientation === 'reversed' ? entry.card.reversed : entry.card.upright;
    button.setAttribute('aria-label', `${entry.position} card revealed: ${entry.card.name}, ${titleCase(entry.orientation)}`);
    image.classList.toggle('is-reversed', entry.orientation === 'reversed');
    image.onerror = () => image.classList.add('is-image-fallback');
    image.src = this.cardUrls[entry.card.id] ?? this.cardUrls.back;
    image.alt = `${entry.card.name}, ${titleCase(entry.orientation)}`;
    name.textContent = entry.card.name;
    orientation.textContent = titleCase(entry.orientation);
    keywords.textContent = cardKeywords.slice(0, 3).join(' · ');
  }

  preloadCardImage(url) {
    const ImageConstructor = this.ownerDocument?.defaultView?.Image ?? globalThis.Image;
    if (!url || typeof ImageConstructor !== 'function') return Promise.resolve();

    return new Promise((resolve) => {
      const image = new ImageConstructor();
      let settled = false;
      const finish = async () => {
        if (settled) return;
        settled = true;
        try { await image.decode?.(); } catch { /* The existing image fallback remains available. */ }
        resolve();
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      image.onload = finish;
      image.onerror = fail;
      image.src = url;
      if (image.complete) {
        if (image.naturalWidth > 0) void finish();
        else fail();
      }
    });
  }

  prepareSpreadImages() {
    const urls = this.spread.map((entry) => this.cardUrls[entry.card.id] ?? this.cardUrls.back);
    const loadKey = urls.join('|');
    if (urls.length !== 3 || this.allCardsRevealed) {
      this.cardImageLoadToken = null;
      this.cardImageLoadKey = loadKey;
      this.cardImageLoadPromise = null;
      this.cardImagesReady = urls.length === 3;
      return Promise.resolve();
    }
    if (loadKey === this.cardImageLoadKey && (this.cardImagesReady || this.cardImageLoadPromise)) {
      return this.cardImageLoadPromise ?? Promise.resolve();
    }

    const loadToken = {};
    this.cardImageLoadToken = loadToken;
    this.cardImageLoadKey = loadKey;
    this.cardImagesReady = false;
    this.revealProgress.textContent = 'Preparing your cards…';
    this.cardImageLoadPromise = Promise.all(urls.map((url) => this.preloadCardImage(url))).then(() => {
      if (this.cardImageLoadToken !== loadToken) return;
      this.cardImagesReady = true;
      this.cardImageLoadPromise = null;
      this.revealProgress.textContent = 'Your cards are ready to reveal.';
      this.renderSpread();
    });
    return this.cardImageLoadPromise;
  }

  revealCard(index) {
    if (this.requestInFlight || this.cardImagesReady === false) return;
    if (!this.ensureActiveSession()) return;
    const entry = this.spread[index];
    if (!entry || this.state.revealedPositions.includes(entry.position)) return;

    this.state.revealedPositions.push(entry.position);
    if (this.allCardsRevealed) {
      this.state = applyCompletionDefaults(this.state);
      this.applyPackageControls();
    }
    this.save();
    this.renderCardSlot(this.cardSlots[index], entry, true);
    this.revealProgress.textContent = `${entry.position} revealed, ${this.state.revealedPositions.length} of 3`;
    if (this.allCardsRevealed) this.updateRevealGate();
    this.update();
  }

  updateRevealGate() {
    const interpretation = this.interpretation;
    const complete = this.previewIsReady && Boolean(interpretation);
    this.previewFolio.hidden = !complete;
    this.depthBridge.hidden = !complete;
    this.chooseReadingButton.hidden = !complete;
    this.chooseReadingButton.disabled = Boolean(this.requestInFlight) || !complete;
    [...this.depthInputs, ...this.deliveryInputs].forEach((input) => {
      input.disabled = Boolean(this.requestInFlight) || !complete;
    });

    if (!complete) {
      if (this.questionPreview) this.questionPreview.textContent = '';
      if (this.preview) this.preview.textContent = '';
      return;
    }

    if (this.questionPreview) this.questionPreview.textContent = `“${this.state.question}”`;
    if (this.preview) this.preview.textContent = interpretation;
  }

  applyPackageControls() {
    this.depthInputs.forEach((input) => { input.checked = input.value === this.state.depth; });
    this.deliveryInputs.forEach((input) => { input.checked = input.value === this.state.delivery; });
  }

  restoreConfigurationControls() {
    const themeInput = this.themeInputs.find((input) => input.value === this.state.theme);
    this.themeInputs.forEach((input) => { input.checked = input === themeInput; });
    this.questionInput.value = this.state.question;
    this.contextInput.value = this.state.context;
    this.applyPackageControls();
  }

  guardLockedInteraction(event) {
    if (!this.requestInFlight) return false;
    event?.preventDefault();
    this.restoreConfigurationControls();
    return true;
  }

  syncInteractionLock() {
    const locked = Boolean(this.requestInFlight);
    const presetButtons = [...this.presets.querySelectorAll('button')];
    const alwaysMutable = [
      ...this.themeInputs,
      this.questionInput,
      this.contextInput,
      this.drawCardsButton,
      ...this.navigationButtons,
      ...presetButtons,
      ...this.startOverButtons
    ].filter(Boolean);
    const focusableControls = [
      ...alwaysMutable,
      ...this.depthInputs,
      ...this.deliveryInputs,
      ...this.cardRevealButtons,
      this.submit
    ];

    if (locked && !this.interactionLockApplied && focusableControls.includes(this.ownerDocument.activeElement)) {
      this.status.setAttribute('tabindex', '-1');
      this.status.focus({ preventScroll: true });
    }

    alwaysMutable.forEach((control) => { control.disabled = locked; });
    if (locked) this.submit.setAttribute('aria-busy', 'true');
    else this.submit.removeAttribute('aria-busy');

    const complete = this.previewIsReady;
    [...this.depthInputs, ...this.deliveryInputs].forEach((input) => {
      input.disabled = locked || !complete;
    });
    this.chooseReadingButton.disabled = locked || !complete;
    this.cardRevealButtons.forEach((button, index) => {
      const entry = this.spread[index];
      const revealed = Boolean(entry && this.state.revealedPositions.includes(entry.position));
      const preparing = Boolean(entry) && !revealed && this.cardImagesReady === false;
      button.disabled = locked || !entry || preparing;
      button.setAttribute('aria-disabled', String(locked || !entry || revealed || preparing));
    });
    this.interactionLockApplied = locked;
  }

  updateDrawAction() {
    if (this.drawCardsButton) {
      this.drawCardsButton.textContent = this.state.spread.length !== 3
        ? 'Prepare my three cards'
        : (this.spreadIsStale ? 'Draw for this updated question' : 'Return to my three cards');
    }
    if (this.spreadStaleNotice) this.spreadStaleNotice.hidden = !this.spreadIsStale;
  }

  showStep(number, focus = true) {
    if (this.requestInFlight) return;
    if (number >= 3 && !this.ensureActiveSession(Date.now(), focus)) return;
    if (number >= 3 && this.spreadIsStale) number = 2;
    if (number === 3 && this.state.spread.length !== 3) number = 2;
    if (number === 4 && !this.previewIsReady) {
      number = this.state.spread.length === 3 ? 3 : 2;
      if (this.revealProgress) this.revealProgress.textContent = 'Complete the free interpretation to choose your reading.';
    }
    this.steps.forEach((step) => { step.hidden = Number(step.dataset.readingStep) !== number; });
    this.progress.setAttribute('aria-valuenow', String(number));
    this.querySelectorAll('[data-step-indicator]').forEach((indicator) => {
      if (Number(indicator.dataset.stepIndicator) === number) indicator.setAttribute('aria-current', 'step');
      else indicator.removeAttribute('aria-current');
    });
    if (focus) {
      const heading = this.steps.find((step) => Number(step.dataset.readingStep) === number)?.querySelector('h2');
      heading?.setAttribute('tabindex', '-1');
      heading?.focus({ preventScroll: true });
      heading?.scrollIntoView({ block: 'start', behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }
  }

  get reducedMotion() {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  setError(key, message) {
    const node = this.querySelector(`[data-error-for="${key}"]`);
    if (node) node.textContent = message;
    const wrapper = this.querySelector(`[data-field-wrapper="${key}"]`);
    if (wrapper) wrapper.dataset.state = message ? 'error' : '';
  }

  setProperty(name, value) {
    const input = this.propertyInputs.find((entry) => entry.dataset.property === name);
    if (input) input.value = value ?? '';
  }

  updateCounts() {
    if (this.questionCount) this.questionCount.textContent = String(this.questionInput.value.length);
    if (this.contextCount) this.contextCount.textContent = String(this.contextInput.value.length);
  }

  values() {
    return {
      ...this.state,
      themeLabel: this.theme?.label ?? '',
      spread: this.spread
    };
  }

  update() {
    const values = this.values();
    const state = readingSubmitState(this.variants, values, Boolean(this.loading));
    this.syncInteractionLock();
    this.renderVariantPrices();
    if (state.variant?.available) {
      this.variantInput.value = state.variant.id;
      this.fallbackSelect.value = String(state.variant.id);
    } else {
      this.variantInput.value = '';
      this.fallbackSelect.value = '';
    }
    this.priceNode.textContent = state.priceLabel;
    this.updateOrderPriceBreakdown();
    this.submit.disabled = !state.ready || Boolean(this.requestInFlight);
    this.submit.textContent = state.label;
    this.status.textContent = state.message;
    this.updateDrawAction();
    if (this.orderTitle) {
      this.orderTitle.textContent = state.variant
        ? `${this.state.depth} reading · ${this.state.delivery}`
        : 'Choose a report depth and delivery window.';
    }
    if (this.orderSpread) {
      this.orderSpread.textContent = this.previewIsReady
        ? this.spread.map((entry) => `${entry.position}: ${entry.card.name} (${entry.orientation})`).join(' · ')
        : 'Your question and three cards will stay attached to this order.';
    }
    const properties = lineItemProperties(values);
    Object.entries(properties).forEach(([name, value]) => this.setProperty(name, value));
  }

  renderVariantPrices() {
    const themeLabel = this.theme?.label ?? THEME_DEFINITIONS[0]?.label ?? '';
    this.depthPriceNodes.forEach((node) => {
      const pricing = readingPriceBreakdown(this.variants, node.dataset.depthPrice, '24 hours', themeLabel, this.optionNames);
      node.textContent = pricing?.basePriceLabel ?? 'Unavailable';
    });
    this.deliveryPriceNodes.forEach((node) => {
      const pricing = readingPriceBreakdown(this.variants, 'Focused', node.dataset.deliveryPrice, themeLabel, this.optionNames);
      node.textContent = pricing?.deliveryPriceLabel ?? 'Unavailable';
    });
  }

  updateOrderPriceBreakdown() {
    const themeLabel = this.theme?.label ?? THEME_DEFINITIONS[0]?.label ?? '';
    const pricing = this.state.depth && this.state.delivery
      ? readingPriceBreakdown(this.variants, this.state.depth, this.state.delivery, themeLabel, this.optionNames)
      : null;
    this.orderBaseLabel.textContent = this.state.depth ? `${this.state.depth} report` : 'Report';
    this.orderBasePrice.textContent = pricing?.basePriceLabel ?? 'Choose an option';
    this.orderDeliveryLabel.textContent = this.state.delivery
      ? `${this.state.delivery.replace(' hours', '-hour')} delivery`
      : 'Delivery';
    this.orderDeliveryPrice.textContent = pricing?.deliveryPriceLabel ?? 'Choose an option';
    if (pricing) this.priceNode.textContent = pricing.totalPriceLabel;
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, serializeSession(this.state));
    } catch {
      // The flow remains usable if storage is unavailable.
    }
  }

  clearTransientSubmissionState() {
    this.loading = false;
    if (this.status) this.status.textContent = '';
  }

  reset() {
    if (this.requestInFlight) return;
    this.clearTransientSubmissionState();
    this.cardImageLoadToken = null;
    this.cardImageLoadKey = '';
    this.cardImageLoadPromise = null;
    this.cardImagesReady = false;
    try { localStorage.removeItem(this.storageKey); } catch { /* no-op */ }
    this.state = this.emptyState();
    this.themeInputs.forEach((input) => { input.checked = false; });
    this.depthInputs.forEach((input) => { input.checked = false; });
    this.deliveryInputs.forEach((input) => { input.checked = false; });
    this.questionInput.value = '';
    this.contextInput.value = '';
    ['theme', 'question', 'context', 'depth', 'delivery'].forEach((key) => this.setError(key, ''));
    this.renderTheme();
    this.revealProgress.textContent = '';
    if (this.spreadStaleNotice) this.spreadStaleNotice.hidden = true;
    this.cardSlots.forEach((slot) => this.renderCardSlot(slot, null, false));
    this.updateRevealGate();
    this.updateCounts();
    this.update();
    if (this.summary) this.summary.hidden = true;
    if (this.status) this.status.textContent = '';
    this.showStep(1);
  }

  completeSubmission() {
    try { localStorage.removeItem(this.storageKey); } catch { /* no-op */ }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.requestInFlight || this.loading) return;
    if (!this.ensureActiveSession()) return;
    const submittedValues = this.values();
    const submitState = readingSubmitState(this.variants, submittedValues, Boolean(this.loading));
    if (!submitState.ready) {
      this.update();
      return;
    }

    const requestToken = { stateKey: serializeSession(this.state) };
    const submissionId = createSessionId();
    const submittedProperties = readingSubmissionProperties(submittedValues, submissionId);
    const submittedLine = {
      variantId: String(submitState.variant.id),
      properties: submittedProperties,
      sessionId: submittedValues.sessionId
    };
    this.requestInFlight = requestToken;
    let formData;
    try {
      formData = new FormData(this.form);
    } catch (error) {
      if (this.requestInFlight !== requestToken) return;
      this.requestInFlight = null;
      this.loading = false;
      this.update();
      this.status.textContent = error.message;
      return;
    }
    formData.set('properties[_Reading submission]', submissionId);
    this.loading = true;
    this.update();
    const root = window.Shopify?.routes?.root ?? '/';
    let addSucceeded = false;
    try {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const addPayload = await addResponse.json();
      if (!addResponse.ok) throw new Error(addPayload.description ?? 'The reading could not be added to cart.');
      addSucceeded = true;

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      const cartPayload = await cartResponse.json();
      if (!cartResponse.ok) throw new Error(cartPayload.description ?? 'The cart could not be checked. Please try again.');
      const resolution = readingResolutionForCart(cartPayload, submittedLine, addPayload.key);
      const updateResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates: resolution.updates })
      });
      const updatedCart = await updateResponse.json();
      if (!updateResponse.ok) throw new Error(updatedCart.description ?? 'The reading cart lines could not be resolved.');
      if (!cartContainsResolvedReading(updatedCart, submittedLine, resolution)) {
        throw new Error('The reading cart could not be verified after duplicate resolution.');
      }
      if (this.requestInFlight !== requestToken) return;
      this.requestInFlight = null;
      if (serializeSession(this.state) !== requestToken.stateKey) {
        this.loading = false;
        this.update();
        return;
      }
      this.completeSubmission();
      window.location.assign(`${root}cart`);
    } catch (error) {
      if (this.requestInFlight !== requestToken) return;
      this.requestInFlight = null;
      const requestStateIsCurrent = serializeSession(this.state) === requestToken.stateKey;
      this.loading = false;
      this.update();
      if (requestStateIsCurrent) {
        this.status.textContent = addSucceeded
          ? 'Your reading was added, but cart preparation did not finish. Please try again.'
          : error.message;
      }
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('reading-configurator')) {
  customElements.define('reading-configurator', ReadingConfigurator);
}
